/**
 * Victorian Sliders - Quotes and Orders Types
 * Based on Trade Counter Process Document
 */

// ============================================
// QUOTE STATUS
// ============================================

export enum QuoteStatus {
  LIVE = 'live',
  FOLLOW_UP_NEEDED = 'follow_up_needed',
  PROJECT = 'project',
  CONVERTED = 'converted',
  LOST = 'lost',
  EXPIRED = 'expired',
}

export const QuoteStatusLabels: Record<QuoteStatus, string> = {
  [QuoteStatus.LIVE]: 'Live',
  [QuoteStatus.FOLLOW_UP_NEEDED]: 'Follow-up Needed',
  [QuoteStatus.PROJECT]: 'Project',
  [QuoteStatus.CONVERTED]: 'Converted',
  [QuoteStatus.LOST]: 'Lost',
  [QuoteStatus.EXPIRED]: 'Expired',
};

// ============================================
// FOLLOW-UP ACTION TYPES
// ============================================

export enum FollowUpActionType {
  CALLBACK = 'callback',
  SMS = 'sms',
  EMAIL = 'email',
  SET_DATE = 'set_date',
}

export const FollowUpActionLabels: Record<FollowUpActionType, string> = {
  [FollowUpActionType.CALLBACK]: 'Callback',
  [FollowUpActionType.SMS]: 'SMS',
  [FollowUpActionType.EMAIL]: 'Email',
  [FollowUpActionType.SET_DATE]: 'Set a Date',
};

export interface FollowUpAction {
  id: string;
  type: FollowUpActionType;
  date?: Date;
  notes?: string;
  assignedBy: string;
  assignedAt: Date;
  completed: boolean;
  completedAt?: Date;
}

// ============================================
// PROJECT PRIORITY
// ============================================

export enum ProjectPriority {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export const ProjectPriorityLabels: Record<ProjectPriority, string> = {
  [ProjectPriority.HIGH]: 'High',
  [ProjectPriority.MEDIUM]: 'Medium',
  [ProjectPriority.LOW]: 'Low',
};

export interface ProjectDetails {
  id: string;
  inputOnSalesTracker: boolean;
  salesTrackerUrl?: string;
  expectedCloseDate?: Date;
  projectValue?: number;
  assignedRSM?: string;
  priority: ProjectPriority;
  notes?: string;
}

// ============================================
// PAYMENT STATUS
// ============================================

export enum PaymentStatus {
  PENDING = 'pending',
  DEPOSIT_PAID = 'deposit_paid',
  PARTIALLY_PAID = 'partially_paid',
  FULLY_PAID = 'fully_paid',
  OVERDUE = 'overdue',
}

export const PaymentStatusLabels: Record<PaymentStatus, string> = {
  [PaymentStatus.PENDING]: 'Pending',
  [PaymentStatus.DEPOSIT_PAID]: 'Deposit Paid',
  [PaymentStatus.PARTIALLY_PAID]: 'Partially Paid',
  [PaymentStatus.FULLY_PAID]: 'Fully Paid',
  [PaymentStatus.OVERDUE]: 'Overdue',
};

// ============================================
// PRODUCTION STATUS
// ============================================

export enum ProductionStatus {
  NOT_STARTED = 'not_started',
  IN_PRODUCTION = 'in_production',
  QUALITY_CHECK = 'quality_check',
  READY_FOR_DISPATCH = 'ready_for_dispatch',
  DISPATCHED = 'dispatched',
  DELIVERED = 'delivered',
}

export const ProductionStatusLabels: Record<ProductionStatus, string> = {
  [ProductionStatus.NOT_STARTED]: 'Not Started',
  [ProductionStatus.IN_PRODUCTION]: 'In Production',
  [ProductionStatus.QUALITY_CHECK]: 'Quality Check',
  [ProductionStatus.READY_FOR_DISPATCH]: 'Ready for Dispatch',
  [ProductionStatus.DISPATCHED]: 'Dispatched',
  [ProductionStatus.DELIVERED]: 'Delivered',
};

// ============================================
// PROFILE COLOUR
// ============================================

export interface ProfileColour {
  colour: string;
  count: number;
}

// ============================================
// QUOTE INTERFACE
// ============================================

export interface Quote {
  id: string;
  quoteNumber: string; // Format: Q-YYYY-#### (TBC)
  customerName: string;
  customerCompany: string;
  customerReference?: string; // Customer's internal reference
  area: string; // Region
  dateQuoted: Date;
  numberOfFrames: number;
  profileColours: ProfileColour[]; // Array of colours with counts
  mostCommonColour: string; // Most common colour
  quoteValue: number; // Final customer price
  baseCost?: number; // Cost from Victorian Sliders
  margin?: number; // Markup applied
  status: QuoteStatus;

  // NEW indicator fields
  isNew: boolean; // NEW indicator (per user)
  viewedAt?: Date; // When first viewed
  viewedBy: string[]; // Array of user IDs who viewed
  markedAsReadAt?: Date; // When marked as read by current user
  lastSignificantUpdate?: Date; // Last status/value change

  // Follow-up details
  followUpAction?: FollowUpAction;

  // Project details
  projectDetails?: ProjectDetails;

  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  notes?: string;

  // Activity log
  activityLog: ActivityLogEntry[];
}

// ============================================
// ORDER INTERFACE
// ============================================

export interface Order {
  id: string;
  orderNumber: string;
  quoteId?: string; // Link to original quote
  customerName: string;
  customerCompany: string;
  customerReference?: string;
  area: string;
  dateOrdered: Date;
  numberOfFrames: number;
  orderValue: number;
  paymentStatus: PaymentStatus;
  productionStatus: ProductionStatus;
  deliveryDate?: Date;

  // NEW indicator fields
  isNew: boolean; // NEW indicator (per user)
  viewedAt?: Date; // When first viewed
  viewedBy: string[]; // Array of user IDs who viewed
  markedAsReadAt?: Date; // When marked as read by current user
  lastSignificantUpdate?: Date; // Last payment/production change

  // Payment details
  depositAmount?: number;
  depositPaidAt?: Date;
  balanceAmount?: number;
  balancePaidAt?: Date;

  // Production tracking
  productionStartDate?: Date;
  productionCompletionDate?: Date;
  dispatchDate?: Date;

  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  notes?: string;

  // Activity log
  activityLog: ActivityLogEntry[];
}

// ============================================
// ACTIVITY LOG
// ============================================

export enum ActivityType {
  CREATED = 'created',
  STATUS_CHANGED = 'status_changed',
  FOLLOW_UP_ADDED = 'follow_up_added',
  FOLLOW_UP_COMPLETED = 'follow_up_completed',
  PROJECT_UPDATED = 'project_updated',
  CONVERTED_TO_ORDER = 'converted_to_order',
  PAYMENT_RECEIVED = 'payment_received',
  PRODUCTION_UPDATED = 'production_updated',
  NOTE_ADDED = 'note_added',
  VIEWED = 'viewed',
}

export interface ActivityLogEntry {
  id: string;
  type: ActivityType;
  description: string;
  performedBy: string;
  performedAt: Date;
  metadata?: Record<string, any>;
}

// ============================================
// SEARCH FILTERS
// ============================================

export interface QuoteFilters {
  searchQuery?: string;
  status?: QuoteStatus | 'all';
  area?: string | 'all';
  dateFrom?: Date;
  dateTo?: Date;
  valueMin?: number;
  valueMax?: number;
  showOnlyNew?: boolean;
}

export interface OrderFilters {
  searchQuery?: string;
  paymentStatus?: PaymentStatus | 'all';
  productionStatus?: ProductionStatus | 'all';
  area?: string | 'all';
  dateFrom?: Date;
  dateTo?: Date;
  valueMin?: number;
  valueMax?: number;
  showOnlyNew?: boolean;
}

// ============================================
// STATISTICS
// ============================================

export interface QuoteStatistics {
  total: number;
  new: number;
  live: number;
  followUpNeeded: number;
  project: number;
  converted: number;
  lost: number;
  expired: number;
  totalValue: number;
}

export interface OrderStatistics {
  total: number;
  new: number;
  pending: number;
  inProduction: number;
  readyForDispatch: number;
  dispatched: number;
  delivered: number;
  totalValue: number;
  outstandingPayments: number;
}

// ============================================
// QUOTE NUMBERING CONFIG (TBC)
// ============================================

export interface QuoteNumberingConfig {
  prefix: string; // e.g., "Q"
  yearFormat: 'YYYY' | 'YY';
  separator: string; // e.g., "-"
  digitCount: number; // e.g., 4 for ####
  resetYearly: boolean;
  customPrefixPerCustomer?: boolean;
}

export const DEFAULT_QUOTE_NUMBERING: QuoteNumberingConfig = {
  prefix: 'Q',
  yearFormat: 'YYYY',
  separator: '-',
  digitCount: 4,
  resetYearly: true,
  customPrefixPerCustomer: false,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Generate quote number based on config
 * NOTE: Format to be confirmed with client
 */
export function generateQuoteNumber(
  sequenceNumber: number,
  config: QuoteNumberingConfig = DEFAULT_QUOTE_NUMBERING
): string {
  const year = config.yearFormat === 'YYYY'
    ? new Date().getFullYear().toString()
    : new Date().getFullYear().toString().slice(-2);

  const paddedNumber = sequenceNumber.toString().padStart(config.digitCount, '0');

  return `${config.prefix}${config.separator}${year}${config.separator}${paddedNumber}`;
}

/**
 * Get most common colour from profile colours
 */
export function getMostCommonColour(colours: ProfileColour[]): string {
  if (colours.length === 0) return 'N/A';
  if (colours.length === 1) return colours[0]?.colour || 'N/A';

  const sorted = [...colours].sort((a, b) => b.count - a.count);
  if (sorted.length === 0) return 'N/A';

  const max = sorted[0]?.count || 0;
  const maxCount = sorted.filter(c => c.count === max).length;

  if (maxCount > 1) {
    return `Mixed (${colours.length} colours)`;
  }

  return sorted[0]?.colour || 'N/A';
}

/**
 * Check if quote/order is high value
 */
export function isHighValue(value: number, threshold: number = 5000): boolean {
  return value >= threshold;
}

/**
 * Calculate quote margin percentage
 */
export function calculateMarginPercentage(quoteValue: number, baseCost: number): number {
  if (baseCost === 0) return 0;
  return ((quoteValue - baseCost) / baseCost) * 100;
}

/**
 * Get status badge color
 */
export function getQuoteStatusColor(status: QuoteStatus): string {
  switch (status) {
    case QuoteStatus.LIVE:
      return '#16a34a'; // vivid green
    case QuoteStatus.FOLLOW_UP_NEEDED:
      return '#f97316'; // vivid orange
    case QuoteStatus.PROJECT:
      return '#2563eb'; // vivid blue
    case QuoteStatus.CONVERTED:
      return '#a855f7'; // vivid purple
    case QuoteStatus.LOST:
      return '#dc2626'; // vivid red
    case QuoteStatus.EXPIRED:
      return '#6b7280'; // stronger gray
    default:
      return '#6b7280';
  }
}

/**
 * Get payment status color
 */
export function getPaymentStatusColor(status: PaymentStatus): string {
  switch (status) {
    case PaymentStatus.FULLY_PAID:
      return '#10b981'; // Green
    case PaymentStatus.DEPOSIT_PAID:
    case PaymentStatus.PARTIALLY_PAID:
      return '#f59e0b'; // Orange
    case PaymentStatus.OVERDUE:
      return '#ef4444'; // Red
    case PaymentStatus.PENDING:
      return '#9ca3af'; // Gray
    default:
      return '#6b7280';
  }
}

/**
 * Get production status color
 */
export function getProductionStatusColor(status: ProductionStatus): string {
  switch (status) {
    case ProductionStatus.DELIVERED:
      return '#10b981'; // Green
    case ProductionStatus.DISPATCHED:
    case ProductionStatus.READY_FOR_DISPATCH:
      return '#8b5cf6'; // Purple
    case ProductionStatus.IN_PRODUCTION:
    case ProductionStatus.QUALITY_CHECK:
      return '#f59e0b'; // Orange
    case ProductionStatus.NOT_STARTED:
      return '#9ca3af'; // Gray
    default:
      return '#6b7280';
  }
}

// ============================================
// NEW INDICATOR HELPERS
// ============================================

/**
 * Check if quote/order is NEW for a specific user
 * Definition: Created within last X hours AND user hasn't viewed it
 */
export function isNewForUser(
  item: Quote | Order,
  userId: string,
  newThresholdHours: number = 24
): boolean {
  // Check if user has already viewed
  if (item.viewedBy.includes(userId)) {
    return false;
  }

  // Check if created within threshold
  const now = new Date();
  const createdAt = new Date(item.createdAt);
  const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

  if (hoursSinceCreation > newThresholdHours) {
    return false;
  }

  // Check if updated since last view (if user viewed before)
  if (item.lastSignificantUpdate) {
    const lastUpdate = new Date(item.lastSignificantUpdate);
    if (item.viewedAt && lastUpdate > item.viewedAt) {
      return true; // Updated since last view, so it's NEW again
    }
  }

  return true;
}

/**
 * Mark quote/order as viewed by user
 */
export function markAsViewed(item: Quote | Order, userId: string): Quote | Order {
  if (item.viewedBy.includes(userId)) {
    return item; // Already viewed
  }

  return {
    ...item,
    viewedBy: [...item.viewedBy, userId],
    viewedAt: item.viewedAt || new Date(),
    markedAsReadAt: new Date(),
    isNew: false,
  };
}

/**
 * Get NEW count for user
 */
export function getNewCountForUser(items: (Quote | Order)[], userId: string, thresholdHours: number = 24): number {
  return items.filter((item) => isNewForUser(item, userId, thresholdHours)).length;
}

/**
 * Check if item should auto-expire as NEW
 * Returns true if created more than threshold hours ago
 */
export function shouldAutoExpireNew(item: Quote | Order, thresholdHours: number = 24): boolean {
  const now = new Date();
  const createdAt = new Date(item.createdAt);
  const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
  return hoursSinceCreation > thresholdHours;
}
